import React from 'react';
import {Image, TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import InterestingMove from '@/Assets/Annotation/interesting_move.svg';
import {styles} from '../../styles';
import {AUTOPLAY_SPEEDS} from '../../constants/autoplay';

type NavArrowsProps = {
  // Next/Last/autoplay would let a student reveal hidden moves, or skip
  // a lesson/practice chapter's guided flow, without playing them (see
  // handleNextMove/handleLastMove's own guards) - disable them visibly
  // here too, rather than leaving dead buttons.
  forwardBlocked: boolean;
  onFirstMove: () => void;
  onPrevMove: () => void;
  onNextMove: () => void;
  onLastMove: () => void;
  isAutoplayOn: boolean;
  onToggleAutoplay: () => void;
  autoplaySpeed: number;
  onSetAutoplaySpeed: (ms: number) => void;
  showSpeedMenu: boolean;
  onToggleSpeedMenu: () => void;
};

export function NavArrows({
  forwardBlocked,
  onFirstMove,
  onPrevMove,
  onNextMove,
  onLastMove,
  isAutoplayOn,
  onToggleAutoplay,
  autoplaySpeed,
  onSetAutoplaySpeed,
  showSpeedMenu,
  onToggleSpeedMenu,
}: NavArrowsProps) {
  const activeSpeed =
    AUTOPLAY_SPEEDS.find(s => s.ms === autoplaySpeed) || AUTOPLAY_SPEEDS[1];
  return (
    <Box style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
      <TouchableOpacity onPress={onFirstMove} style={{padding: 4}}>
        <Ionicons
          name="play-skip-back"
          size={16}
          color={CCColors.TextColor.Primary}
        />
      </TouchableOpacity>
      <TouchableOpacity onPress={onPrevMove} style={{padding: 4}}>
        <Ionicons
          name="chevron-back"
          size={18}
          color={CCColors.TextColor.Primary}
        />
      </TouchableOpacity>
      <Box style={{flexDirection: 'row', alignItems: 'center'}}>
        <TouchableOpacity
          onPress={() => !forwardBlocked && onToggleAutoplay()}
          disabled={forwardBlocked}
          style={{
            padding: 4,
            backgroundColor: isAutoplayOn
              ? CCColors.Workbook.AquaTint
              : 'transparent',
            borderRadius: 6,
            opacity: forwardBlocked ? 0.35 : 1,
          }}>
          <Ionicons
            name={isAutoplayOn ? 'pause' : 'play'}
            size={18}
            color={isAutoplayOn ? CCColors.Green : CCColors.TextColor.Primary}
          />
        </TouchableOpacity>
        {/* Autoplay speed, lichess-style: a small chip next to play/pause
            opening a speed menu; the chosen speed persists per user. */}
        <TouchableOpacity
          onPress={() => !forwardBlocked && onToggleSpeedMenu()}
          disabled={forwardBlocked}
          accessibilityLabel="Autoplay speed"
          style={{
            paddingVertical: 2,
            paddingHorizontal: 4,
            borderRadius: 5,
            opacity: forwardBlocked ? 0.35 : 1,
            backgroundColor: showSpeedMenu
              ? updateColorWithOpacity(CCColors.TextColor.Dark, 0.06)
              : 'transparent',
          }}>
          <CCText
            fontSize={9}
            fontWeight="semibold"
            color={CCColors.Workbook.Brown}>
            {activeSpeed.ms / 1000}s ▾
          </CCText>
        </TouchableOpacity>
        {showSpeedMenu && (
          <Box
            style={{
              position: 'absolute',
              bottom: '110%',
              left: 0,
              backgroundColor: CCColors.White,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
              paddingVertical: 4,
              shadowColor: '#000',
              shadowOffset: {width: 0, height: 4},
              shadowOpacity: 0.12,
              shadowRadius: 10,
              elevation: 4,
              zIndex: 40,
              minWidth: 120,
            }}>
            {AUTOPLAY_SPEEDS.map(speed => (
              <TouchableOpacity
                key={speed.ms}
                onPress={() => onSetAutoplaySpeed(speed.ms)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  backgroundColor:
                    speed.ms === autoplaySpeed
                      ? CCColors.Workbook.AquaTint
                      : 'transparent',
                }}>
                <CCText
                  fontSize={11}
                  fontWeight={speed.ms === autoplaySpeed ? 'bold' : 'medium'}
                  color={CCColors.TextColor.Primary}>
                  {speed.label}
                </CCText>
                <CCText fontSize={10} color={CCColors.Workbook.Grey}>
                  {speed.ms / 1000}s
                </CCText>
              </TouchableOpacity>
            ))}
          </Box>
        )}
      </Box>
      <TouchableOpacity
        onPress={onNextMove}
        disabled={forwardBlocked}
        style={{padding: 4, opacity: forwardBlocked ? 0.35 : 1}}>
        <Ionicons
          name="chevron-forward"
          size={18}
          color={CCColors.TextColor.Primary}
        />
      </TouchableOpacity>
      <TouchableOpacity
        onPress={onLastMove}
        disabled={forwardBlocked}
        style={{padding: 4, opacity: forwardBlocked ? 0.35 : 1}}>
        <Ionicons
          name="play-skip-forward"
          size={16}
          color={CCColors.TextColor.Primary}
        />
      </TouchableOpacity>
    </Box>
  );
}

type OpeningChipProps = {
  openingName: string;
  // Desktop's board toolbar renders the chip a touch larger than the
  // mobile navigation toolbar.
  large?: boolean;
};

export function OpeningChip({openingName, large}: OpeningChipProps) {
  if (!openingName) {
    return <Box style={{flex: 1}} />;
  }
  return (
    <Box
      style={{
        flex: 1,
        marginHorizontal: large ? 12 : 8,
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Box
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: CCColors.Workbook.AquaTint,
          paddingVertical: 4,
          paddingHorizontal: large ? 12 : 10,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: CCColors.Workbook.AquaLight,
          maxWidth: '100%',
        }}>
        <Ionicons
          name="book"
          size={12}
          color="#A5792F"
          style={{marginRight: 4}}
        />
        <CCText
          fontSize={large ? 11.5 : 11}
          color="#A5792F"
          fontWeight="semibold"
          numberOfLines={1}
          style={{flexShrink: 1}}>
          {openingName}
        </CCText>
      </Box>
    </Box>
  );
}

type ToolIconsRowProps = {
  annotationsActive: boolean;
  onSelectAnnotations: () => void;
  onFlipBoard: () => void;
  onShowHelp: () => void;
};

export function ToolIconsRow({
  annotationsActive,
  onSelectAnnotations,
  onFlipBoard,
  onShowHelp,
}: ToolIconsRowProps) {
  return (
    <Box style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
      <TouchableOpacity
        onPress={onSelectAnnotations}
        style={{
          paddingHorizontal: 6,
          paddingVertical: 4,
          borderRadius: 6,
          backgroundColor: annotationsActive
            ? CCColors.Workbook.AquaTint
            : 'transparent',
        }}>
        <Image
          resizeMode="contain"
          style={
            {
              width: 18,
              height: 18,
              tintColor: annotationsActive ? CCColors.Green : undefined,
            } as any
          }
          source={InterestingMove}
        />
      </TouchableOpacity>

      <TouchableOpacity onPress={onFlipBoard} style={{padding: 4}}>
        <Ionicons
          name="swap-vertical"
          size={18}
          color={CCColors.TextColor.Primary}
        />
      </TouchableOpacity>

      <TouchableOpacity onPress={onShowHelp} style={{padding: 4}}>
        <Ionicons
          name="help-circle-outline"
          size={18}
          color={CCColors.TextColor.Primary}
        />
      </TouchableOpacity>
    </Box>
  );
}

type BoardNavigationToolbarProps = {
  navArrows: NavArrowsProps;
  openingName: string;
  toolIcons: ToolIconsRowProps;
};

// Mobile's combined toolbar: nav arrows, opening chip, tool icons in one
// full-width row under the board.
export function BoardNavigationToolbar({
  navArrows,
  openingName,
  toolIcons,
}: BoardNavigationToolbarProps) {
  return (
    <Box
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        paddingHorizontal: 8,
        paddingVertical: 6,
      }}>
      <NavArrows {...navArrows} />
      <OpeningChip openingName={openingName} />
      <ToolIconsRow {...toolIcons} />
    </Box>
  );
}

type BoardToolbarActionsProps = {
  effectiveIsOwner: boolean;
  isOwner: boolean;
  chapterMode: string | undefined;
  onFlipBoard: () => void;
  showAnnotationsPopover: boolean;
  onToggleAnnotationsPopover: () => void;
  // The popovers stay owned by the screen (they're wired deep into lesson
  // authoring state) and render into the anchor boxes here.
  annotationsPopover: React.ReactNode;
  showLessonPopover: boolean;
  onToggleLessonPopover: () => void;
  lessonPopover: React.ReactNode;
  previewAsParticipant: boolean;
  onTogglePreview: () => void;
};

// Desktop-only: the authoring icon selector without flip/help, which now
// live solely in the header (mobile keeps them together via ToolIconsRow).
export function BoardToolbarActions({
  effectiveIsOwner,
  isOwner,
  chapterMode,
  onFlipBoard,
  showAnnotationsPopover,
  onToggleAnnotationsPopover,
  annotationsPopover,
  showLessonPopover,
  onToggleLessonPopover,
  lessonPopover,
  previewAsParticipant,
  onTogglePreview,
}: BoardToolbarActionsProps) {
  return (
    <Box style={{flexDirection: 'row-reverse', alignItems: 'center', gap: 4}}>
      <TouchableOpacity onPress={onFlipBoard} style={{padding: 4}}>
        <Ionicons
          name="swap-vertical"
          size={18}
          color={CCColors.TextColor.Primary}
        />
      </TouchableOpacity>

      {effectiveIsOwner && (
        <Box style={{position: 'relative'}}>
          <TouchableOpacity
            onPress={onToggleAnnotationsPopover}
            style={{
              padding: 4,
              borderRadius: 6,
              backgroundColor: showAnnotationsPopover
                ? CCColors.Workbook.AquaTint
                : 'transparent',
            }}>
            <Image
              resizeMode="contain"
              style={
                {
                  width: 18,
                  height: 18,
                  tintColor: showAnnotationsPopover
                    ? CCColors.Green
                    : undefined,
                } as any
              }
              source={InterestingMove}
            />
          </TouchableOpacity>
          {showAnnotationsPopover && annotationsPopover}
        </Box>
      )}

      {/* Move guidance/comment authoring for the selected move: role +
          hint/success/wrong-move fields in a lesson chapter, a plain
          comment field everywhere else - so it's available for the owner
          regardless of chapter mode, gated the same way the other
          owner-only board-toolbar actions are. */}
      {effectiveIsOwner && (
        <Box style={{position: 'relative'}}>
          <TouchableOpacity
            onPress={onToggleLessonPopover}
            style={{
              padding: 4,
              borderRadius: 6,
              backgroundColor: showLessonPopover
                ? CCColors.Workbook.AquaTint
                : 'transparent',
            }}>
            <MaterialCommunityIcons
              name="school-outline"
              size={18}
              color={
                showLessonPopover ? CCColors.Green : CCColors.Workbook.Grey
              }
            />
          </TouchableOpacity>
          {showLessonPopover && lessonPopover}
        </Box>
      )}

      {/* Keyed off the real isOwner, not effectiveIsOwner - this is the
          switch that turns preview mode off, so it must stay visible while
          previewAsParticipant is true (effectiveIsOwner goes false the
          instant preview turns on, which would hide this button and strand
          the owner in preview mode with no way back). Same reasoning as
          the chapter/participants management affordances above. */}
      {isOwner && chapterMode !== 'analysis' && (
        <TouchableOpacity onPress={onTogglePreview} style={{padding: 4}}>
          <MaterialCommunityIcons
            name={previewAsParticipant ? 'eye' : 'eye-outline'}
            size={18}
            color={previewAsParticipant ? '#A5792F' : CCColors.Workbook.Grey}
          />
        </TouchableOpacity>
      )}
    </Box>
  );
}

type WorkbookHeaderTabsProps = {
  activeTab: string;
  onChangeTab: (tab: 'moves' | 'analysis' | 'settings') => void;
  isChapterLocked: boolean;
  showSettings: boolean;
};

// Renders inside styles.workbookHeaderTabsBox, up in the page header -
// kept as its own unit so it only has to be dropped into a box of the
// right width/position. Three tabs, each flex:1 so they split the box
// evenly instead of reading as a compact left-aligned cluster.
export function WorkbookHeaderTabs({
  activeTab,
  onChangeTab,
  isChapterLocked,
  showSettings,
}: WorkbookHeaderTabsProps) {
  return (
    <>
      <TouchableOpacity
        onPress={() => onChangeTab('moves')}
        style={[
          styles.workspaceTabBtn,
          {flex: 1},
          activeTab === 'moves' && styles.workspaceTabBtnActive,
        ]}>
        <MaterialCommunityIcons
          name="format-list-numbered"
          size={15}
          color={activeTab === 'moves' ? '#A5792F' : CCColors.Workbook.Grey}
        />
        <CCText
          style={[
            styles.workspaceTabLabel,
            activeTab === 'moves' && styles.workspaceTabLabelActive,
          ]}>
          Moves
        </CCText>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={() => onChangeTab('analysis')}
        disabled={isChapterLocked}
        style={[
          styles.workspaceTabBtn,
          {flex: 1},
          activeTab === 'analysis' && styles.workspaceTabBtnActive,
          isChapterLocked && {opacity: 0.5},
        ]}>
        <Ionicons
          name="stats-chart-outline"
          size={14}
          color={activeTab === 'analysis' ? '#A5792F' : CCColors.Workbook.Grey}
        />
        <CCText
          style={[
            styles.workspaceTabLabel,
            activeTab === 'analysis' && styles.workspaceTabLabelActive,
          ]}>
          Analysis
        </CCText>
      </TouchableOpacity>
      {showSettings && (
        <TouchableOpacity
          onPress={() => onChangeTab('settings')}
          style={[
            styles.workspaceTabBtn,
            {flex: 1},
            activeTab === 'settings' && styles.workspaceTabBtnActive,
          ]}>
          <Ionicons
            name="settings-outline"
            size={14}
            color={
              activeTab === 'settings' ? '#A5792F' : CCColors.Workbook.Grey
            }
          />
          <CCText
            style={[
              styles.workspaceTabLabel,
              activeTab === 'settings' && styles.workspaceTabLabelActive,
            ]}>
            Settings
          </CCText>
        </TouchableOpacity>
      )}
    </>
  );
}
